<!-- @format -->
<script setup>
import SmartBox from '../common/SmartBox.vue';
import DestinationSearch from './components/DestinationSearch.vue';
import Occupancy from './components/Occupancy.vue';
import SearchInput from './components/SearchInput.vue';
import BookerCalendar from './components/BookerCalendar.vue';
import BookerTracker from "../../MetricsTracker/booker/metrics";
import { storeToRefs } from 'pinia'
import { useFlightBookerStore } from '../stores/flightBooker'
import { ref, getCurrentInstance, onMounted, computed } from 'vue'

const { proxy } = getCurrentInstance()
const __ = proxy.__
const fn = window.__pt.fn;
const config = window.__pt.settings.site;
const _tracker = new BookerTracker();
const defaultDate = fn.getDate();
const guestsBox = ref(null)
const calendarBox = ref(null);
const isSwapped = ref(false);
const isMobile = ref(false);
const store = useFlightBookerStore()
const {
  form,
  fieldErrors,
  book,
  recentSearches,
  popularDestinations,
  searchResults,
  algoliaUrl,
  OpenSearch,
  CloseSearch,
  lastQuery,
  settingPaxes,
  localeDatePicker
} = storeToRefs(store)

const calendarDates = computed({
  get() {
    return {
      start: book.value.checkIn ? new Date(book.value.checkIn) : null,
      end: book.value.checkOut ? new Date(book.value.checkOut) : null
    };
  },
  set(val) {
    book.value.checkIn = val.start ? new Date(val.start) : null;
    book.value.checkOut = val.end ? new Date(val.end) : null;
    updateFormDates(book.value.checkIn, book.value.checkOut);
  }
});

function onSelectDates(val) {
  updateFormDates(val.start, val.end);
  book.value.checkIn = val.start;
  book.value.checkOut = val.end;
  if (calendarBox.value) {
    calendarBox.value.close();
  }
}

let origin = null;
function isValidDate(dateFrom, dateTo) {
  let valid = true;
  dateFrom.setHours(0, 0, 0, 0);
  if (+dateFrom > +dateTo) {
    valid = false;
  }
  return valid;
}

function initDatePicker() {
  let valid = isValidDate(book.value.checkIn, book.value.checkOut);
  if (!valid) {
    book.value.checkOut = store.getInitialDate(book.value.checkIn, config.bookerDefaultCheckOutDay);
  }
  updateFormDates(book.value.checkIn, book.value.checkOut);
}

function updateFormDates(start, end) {
  if (!start) return;
  const format = localeDatePicker.value.format;
  if (form.value.type && end) {
    form.value.dates = `${moment(start).format(format).replace(/\./g, '')} - ${moment(end).format(format).replace(/\./g, '')}`;
  } else {
    form.value.dates = `${moment(start).format(format).replace(/\./g, '')}`;
  }
}

function onOpenSearch(element) {
  setTimeout(() => {
    store.fetchPopularDestinations(element, origin);
  }, 200);
}

async function onSearch(value, mobile, list) {
  if (mobile && list) {
    form.value[list] = value;
  }
  await store.fetchSearchResults(value, mobile, list);
}

function onSelectDestination(item, type, suffix = '') {
  const code = store.selectDestination(item, type, suffix);
  store.setFieldError(type, "");
  if (type === 'from') {
    origin = code;
  }
}

function onChangePaxes(payload) {
  book.value.paxes = payload || book.value.paxes;
  store.updateInputPaxes();
}

function onApplyPaxes(payload) {
  book.value.paxes = payload || book.value.paxes;
  store.updateInputPaxes();
  // isValidFormApply();
  if (guestsBox.value && typeof guestsBox.value.close === 'function') {
    guestsBox.value.close();
  }
}

function onCancelPaxes() {
  store.updateInputPaxes();
  if (guestsBox.value && typeof guestsBox.value.close === 'function') {
    guestsBox.value.close();
  }
}

function onCloseGuests() {
  store.updateInputPaxes();
}
function isValidFormApply() {
  if (store.validateForm(true)) {
    submitSearch();
  }
}
function submitSearch() {
  store.submitSearch();
}

function ClickChange() {
  isSwapped.value = !isSwapped.value;

  if (book.value.place_name || book.value.place_name_to) {
    _tracker.metricBookerExchangeFlights();
    const tmpPlace = book.value.place_name;
    const tmpSelected = book.value.place_selected;

    book.value.place_name = book.value.place_name_to || "";
    book.value.place_selected = book.value.place_selected_to || {};
    form.value.from = book.value.place_name;

    book.value.place_name_to = tmpPlace || "";
    book.value.place_selected_to = tmpSelected || {};
    form.value.to = book.value.place_name_to;
  }
}
function onChangeFlight() {
  updateFormDates(book.value.checkIn, book.value.checkOut);
}
const hasDataFrom = computed(() => {
  return (searchResults.value?.from?.length || 0) > 0 ||
    (recentSearches.value?.from?.length || 0) > 0 ||
    (popularDestinations.value?.from?.length || 0) > 0;
})

const hasDataTo = computed(() => {
  return (searchResults.value?.to?.length || 0) > 0 ||
    (recentSearches.value?.to?.length || 0) > 0 ||
    (popularDestinations.value?.to?.length || 0) > 0;
})

onMounted(() => {
  store.initialDefaultDate();
  store.initSearchBox();
  initDatePicker();
  store.loadSearchHistory();
})
</script>
<template>
  <form class="search" @submit.prevent="submitSearch" :aria-label="__('booker.flights')">
    <div class="search__filters">
      <div class="group">
        <legend class="group__legend font-14 font-400 font-strong">
          {{ __('booker.type') }}
        </legend>
        <ul class="group__list">
          <li class="group__item">
            <label class="option">
              <input type="radio" class="option__check input--radio" name="flightType" :value="false"
                v-on:change="onChangeFlight" v-model="form.type" />
              <span class="option__pill font-12 font-md-14 font-400 font-strong">{{ __('booker.one_way' + '_' +
                config.siteName) }}</span>
            </label>
          </li>
          <li class="group__item">
            <label class="option">
              <input type="radio" class="option__check input--radio" name="flightType" :value="true"
                v-on:change="onChangeFlight" v-model="form.type" />
              <span class="option__pill font-12 font-md-14 font-400 font-strong">{{ __('booker.round_trip' + '_' +
                config.siteName) }}</span>
            </label>
          </li>
        </ul>
      </div>
    </div>
    <div class="search__inputs">
      <div class="search__route">
        <SmartBox @open="onOpenSearch('from')" :title="__('booker.origin_sub')" :OpenBox="OpenSearch.from"
          :CloseBox="CloseSearch.from" :isHeaderActive="false" :hasContent="hasDataFrom" @isMobile="isMobile = $event">
          <template #trigger>
            <SearchInput id="origin" :label="__('booker.origin')" :placeholder="__('booker.origin')" v-model="form.from"
              :selectOnFocus="!isMobile" :activeFocus="!isMobile" @input="onSearch($event, false, 'from')"
              :debounce="700" :autocomplete="'off'" :spellcheck="false" :error="!!fieldErrors.from"
              :errorMessage="fieldErrors.from" :readonly="isMobile">
              <template #icon>
                <span class="search__icon font-20 icons-plane-departure"></span>
              </template>
            </SearchInput>
          </template>
          <template #content>
            <DestinationSearch :product="'F'" :recentSearches="recentSearches.from" :origin="'from'"
              :popularDestinations="popularDestinations.from" @update:query="onSearch($event, true, 'from')"
              :typeBooker="'from'" :destination="form.from" :imgUrl="algoliaUrl" :searchResults="searchResults.from"
              @update:selectedDestination="onSelectDestination($event, 'from', '')" :lastQuery="lastQuery"
              :label="__('booker.origin')" :placeholder="''" :icon="'icons-plane-departure'">
            </DestinationSearch>
          </template>
        </SmartBox>
        <button type="button" class="search__swap" :class="{ 'is-swapped': isSwapped }"
          aria-label="Intercambiar origen y destino" @click="ClickChange">
          <span class="search__swap-icon font-24 font-md-20 icons-arrow-right-arrow-left-regular-full"></span>
        </button>
        <SmartBox @open="onOpenSearch('to')" :title="__('booker.destination_sub')" :OpenBox="OpenSearch.to"
          :CloseBox="CloseSearch.to" :isHeaderActive="false" :hasContent="hasDataTo" @isMobile="isMobile = $event">
          <template #trigger>
            <SearchInput id="destination" :label="__('booker.destination')" :placeholder="__('booker.destination')"
              :selectOnFocus="!isMobile" :activeFocus="!isMobile" v-model="form.to"
              @input="onSearch($event, false, 'to')" :debounce="700" :autocomplete="'off'" :spellcheck="false"
              :error="!!fieldErrors.to" :errorMessage="fieldErrors.to" :readonly="isMobile">
              <template #icon>
                <span class="search__icon font-20 icons-plane-arrival"></span>
              </template>
            </SearchInput>
          </template>
          <template #content>
            <DestinationSearch :product="'F'" :recentSearches="recentSearches.to"
              :popularDestinations="popularDestinations.to" @update:query="onSearch($event, true, 'to')"
              :destination="form.to" :imgUrl="algoliaUrl" :searchResults="searchResults.to"
              @update:selectedDestination="onSelectDestination($event, 'to', '_to')" :lastQuery="lastQuery"
              :typeBooker="'to'" :label="__('booker.destination')" :placeholder="''" :icon="'icons-plane-arrival'">
            </DestinationSearch>
          </template>
        </SmartBox>
      </div>
      <div class="search__details">
        <SmartBox ref="calendarBox" :title="form.type ? __('booker.dates') : __('booker.date')" :isHeaderActive="false"
          :mobile-breakpoint="768">
          <template #trigger>
            <SearchInput id="calendar-checkIn-flight" type="text" required name="calendar-checkIn-flight"
              :activeFocus="true" :label="form.type ? __('booker.dates') : __('booker.date')" :readonly="true"
              v-model="form.dates" :error="!!fieldErrors.dates" :errorMessage="fieldErrors.dates">
              <template #icon>
                <span class="icon font-icons icons-calendar-depart font-20"></span>
              </template>
            </SearchInput>
          </template>
          <template #content>
            <BookerCalendar :modelValue="calendarDates" :currency="config.currency"
              :mode="form.type ? 'range' : 'single'" :unit="'days'" :minDays="1" :minDate="defaultDate"
              :startLabel="__('booker.departure')" :endLabel="__('booker.return')" @select="onSelectDates"
              @close="calendarBox.close()" />
          </template>
        </SmartBox>
        <SmartBox ref="guestsBox" :title="__('booker.passengers_sub')" :isHeaderActive="false" @close="onCloseGuests"
          :mobile-breakpoint="768">
          <template #trigger>
            <SearchInput id="guests" :label="__('booker.passengers')" :placeholder="form.guests" :readonly="true"
              :activeFocus="true" v-model="form.guests" :error="!!fieldErrors.guests"
              :errorMessage="fieldErrors.guests">
              <template #icon>
                <span class="icon font-icons icons-guests font-20"></span>
              </template>
            </SearchInput>
          </template>
          <template #content>
            <Occupancy :product="'F'" :settingPaxes="settingPaxes" :counts="book.paxes" @change="onChangePaxes"
              @apply="onApplyPaxes" @cancel="onCancelPaxes">
            </Occupancy>
          </template>
        </SmartBox>
      </div>
    </div>
    <div class="search__action search__action--mobile">
      <button type="submit" class="search__btn btnPrimary btnPrimary--md">
        {{ __('booker.button') }}
      </button>
    </div>
  </form>
</template>

<style scoped lang="scss">
.search {
  background-color: var(--bg-info-strong);
  padding: var(--space-16);
  border-radius: var(--space-12);
  display: flex;
  flex-direction: column;
  background-color: var(--bg-base);
  border: 1px solid var(--border-subtle);
  margin: var(--space-16);
  gap: var(--space-12);
  box-shadow: var(--shadow-200);

  @media (min-width: 576px) {
    width: 544px;
    margin: var(--space-16) auto;
  }

  @media (min-width: 768px) {
    width: auto;
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-areas:
      'flights flights'
      'flights flights'
      'filters action';
    gap: var(--space-20);
    width: 100%;
    padding-inline: 0px;
    border-radius: 0px 0px var(--space-12) var(--space-12);
    box-shadow: none;
    border: 0px;
    margin: 0px auto;
  }

  /* DESKTOP LAYOUT (> 1280px) */
  @media (min-width: 1280px) {
    grid-template-areas:
      'flights flights action'
      'filters filters filters';
    gap: var(--space-12);
    padding: var(--space-20);
    width: 100%;
    border-radius: var(--space-12);
    border: 1px solid var(--border-subtle);
  }

  &__icon {
    display: flex;
  }

  &__inputs {
    display: flex;
    flex-direction: column;
    gap: var(--space-16);

    /* TABLET (> 768px) */
    @media (min-width: 768px) {
      grid-area: flights;
      gap: var(--space-20);
    }

    /* DESKTOP (> 1280px) */
    @media (min-width: 1280px) {
      display: grid;
      grid-template-columns: minmax(50%, 556px) minmax(378px, 1fr);
      align-items: center;
      gap: var(--space-12);
    }
  }

  &__route {
    display: flex;
    flex-direction: column;
    gap: var(--space-16);
    position: relative;

    @media (min-width: 768px) {
      flex-direction: row;
      gap: var(--space-4);
      width: 100%;
      align-items: center;
    }

    @media (min-width: 1280px) {
      flex: 1;
    }
  }

  /* Botón Swap */
  &__swap {
    position: absolute;
    right: 24px;
    top: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
    width: 36px;
    height: 36px;
    border-radius: var(--space-4);
    border: 1px solid var(--border-subtle);
    color: var(--bg-info-strong);
    background: var(--bg-base);
    transform: translateY(-50%);
    cursor: pointer;

    @media (min-width: 768px) {
      position: unset;
      min-width: 24px;
      height: 24px;
      transform: none;
    }

    &:hover {
      background-color: var(--bg-primary-subtle);
      color: var(--text-primary);
      border: 1px solid var(--border-primary);
    }

    &-icon {
      display: flex;
      transform: rotate(90deg);
      transition: transform 0.4s ease-in-out;

      @media (min-width: 768px) {
        transform: rotate(0deg);
      }
    }

    &.is-swapped &-icon {
      transform: rotate(270deg);

      @media (min-width: 768px) {
        transform: rotate(180deg);
      }
    }
  }

  &__details {
    display: flex;
    flex-direction: column;
    gap: var(--space-16);

    @media (min-width: 768px) {
      flex-direction: row;
      gap: var(--space-32);

      &>* {
        width: 50%;
      }
    }

    @media (min-width: 1280px) {
      flex: 1;
      gap: var(--space-12);
    }
  }

  &__date,
  &__guests {
    @media (min-width: 1280px) {
      flex: 1;
    }
  }

  &__action {
    @media (min-width: 768px) {
      display: flex;
      justify-content: end;
    }

    @media (min-width: 1280px) {
      grid-area: action;
      width: fit-content;
    }
  }

  &__btn {
    width: 100%;

    @media (min-width: 768px) {
      width: 160px;
    }

    @media (min-width: 1280px) {
      width: 114px;
    }
  }

  &__filters {
    display: flex;
    align-items: center;
  }
}

.group {
  border: none;
  padding: 0px;
  margin: 0px;

  @media (min-width: 768px) {
    display: flex;
    align-items: center;
    gap: var(--space-12);
  }

  &__list {
    display: flex;
    gap: var(--space-16);
    list-style: none;
    padding: 0px;
    margin: 0px;
  }

  &__legend {
    display: none;
    width: fit-content;
    margin: 0px;

    @media (min-width: 768px) {
      display: flex;
    }
  }
}

.option {
  display: flex;
  align-items: center;
  gap: var(--space-8);
  cursor: pointer;
  margin: 0px;

  .input--radio {
    cursor: pointer;
  }
}
</style>
